import { ShowSection } from 'framework/ui/layout';
import { FormShowTemplate, FormFieldsData } from 'framework/form';
import React, { useMemo } from 'react';
import RecipientsList from './RecipientsList';
import { Recipient, RecipientType } from 'domain/shared/newProject';
import useCustomerReportService from 'domain/projects/newProject/customerReport/useCustomerReportService';
import { EditableListFormShowTemplate } from 'framework/list';

interface RecipientsContextProps {
  projectId: string;
  reportRecipients?: Recipient[];
}

const buildRecipientsData = (recipients?: Recipient[]) => ({
  recipientsData: [
    {
      id: 'recipients-block',
      mainRecipients: recipients?.filter(r => r.type === RecipientType.MAIN) || [],
      ccRecipients: recipients?.filter(r => r.type === RecipientType.CC) || [],
    },
  ],
});

const RecipientsContext = ({ projectId, reportRecipients }: RecipientsContextProps) => {
  const { batchRecipients, getRecipientsValidationSchema } = useCustomerReportService();

  const inputData = useMemo(() => buildRecipientsData(reportRecipients), [reportRecipients]);

  const transformFormDataToBatch = (formData: FormFieldsData) => {
    const recipientsBlock = formData.recipientsData?.[0];

    if (!recipientsBlock) {
      return { batch: [] };
    }

    const mapRecipients = (recipients: Recipient[], type: RecipientType) =>
      (recipients || []).map(recipient => ({
        id: recipient.id?.startsWith('new-') ? undefined : recipient.id,
        firstName: recipient.firstName,
        lastName: recipient.lastName,
        email: recipient.email,
        type,
        projectId: projectId,
      }));

    const batch = [
      ...mapRecipients(recipientsBlock.mainRecipients, RecipientType.MAIN),
      ...mapRecipients(recipientsBlock.ccRecipients, RecipientType.CC),
    ];

    return { batch };
  };

  const submitFormFunc = async (formData: FormFieldsData) => {
    const batchData = transformFormDataToBatch(formData);

    try {
      const savedRecipients = (await batchRecipients(projectId!, batchData)) as Recipient[];
      return buildRecipientsData(savedRecipients);
    } catch (newError) {
      return Promise.reject(newError);
    }
  };

  return (
    <ShowSection sx={{ '&.section-details__wrapper': { p: '0px !important' } }}>
      <FormShowTemplate
        initialData={inputData}
        getValidationSchemaFunc={getRecipientsValidationSchema}
        submitFormFunc={values => submitFormFunc(values)}
        afterSubmit={{
          message: 'Recipients have been successfully updated.',
        }}
      >
        <EditableListFormShowTemplate listSource="recipientsData">
          <RecipientsList />
        </EditableListFormShowTemplate>
      </FormShowTemplate>
    </ShowSection>
  );
};

export default RecipientsContext;
